import React from 'react';

import styled from 'styled-components';

import Button from '@do/walrus/Button';
import Icon from '@do/walrus/Icon';
import Link from '@do/walrus/Link';
import LoadingState from '@do/walrus/states/Loading';
import Text from '@do/walrus/Text';

import Banner from './Banner';
import Header from './Header';

import { small } from '@do/walrus/MediaQueries';
import { HEADER_HEIGHT } from '../constants';

import colors from '@do/walrus/scss/core/_colors.scss';
import vars from '@do/walrus/scss/core/_variables.scss';
import viewports from '@do/walrus/scss/core/_viewports.scss';

import DOLink from '@do/walrus/DOLink';
import _get from 'lodash/get';
import oauthLogoGithubSrc from '../assets/oauth-logo-github.svg';
import oauthLogoGoogleSrc from '../assets/oauth-logo-google.svg';
import copy from '../copy';

const Wrap = styled.div`
  max-width: ${viewports.xlarge}px;
  position: relative;
  padding: ${HEADER_HEIGHT}px 0 ${vars.whitespace_xlarge};
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  margin: auto;

  ${small} {
    padding-bottom: ${vars.whitespace_large};
  }
`;

const LoadingWrap = styled.div`
  position: absolute;
  left: 50%;
  top: ${HEADER_HEIGHT}px;
  transform: translate(-50%, ${vars.whitespace_xlarge});
`;

const SignInButton = styled(Button)`
  margin-top: ${vars.whitespace_regular};
`;

const FormWrap = styled.div`
  position: relative;
  z-index: ${vars.z_index_base};
  background: ${colors.white};
  box-sizing: border-box;
  border: ${vars.border_width_thin} solid rgba(91, 105, 135, 0.22);
  border-radius: ${vars.border_radius};
  margin-top: 10vh;
  box-shadow: 0px ${vars.space_1} ${vars.space_1} rgba(0, 0, 0, 0.25);
  overflow: auto; /* handle the case when the form is smaller than min-width of TextInput */
  padding-top: ${vars.whitespace_medium};
  padding-bottom: ${vars.whitespace_medium};
  height: 100%;

  ${small} {
    width: ${viewports.small}px;
  }
`;

const PaddedContents = styled.div`
  padding-left: ${vars.whitespace_medium};
  padding-right: ${vars.whitespace_medium};
`;

const GHButton = styled(SignInButton)`
  background-color: ${colors.blue_darkest};

  &:not(.is-disabled):hover {
    background-color: ${colors.grey_dark};
  }
`;

const ButtonImage = styled.img`
  width: ${vars.space_4};
  height: ${vars.space_4};
  margin-top: -${vars.whitespace_xsmall};
  margin-right: ${vars.whitespace_small};
  display: inline-block;
`;

const ButtonImageBg = styled.div`
  display: inline-block;
  vertical-align: middle;
  background: ${colors.white};
  border-radius: ${vars.border_radius};
  height: ${vars.space_5};
  width: ${vars.space_5};
  margin-right: ${vars.whitespace_small};
  margin-top: -2px;

  ${ButtonImage} {
    vertical-align: initial;
    position: relative;
    top: 1px;
    left: ${vars.whitespace_xsmall};
  }
`;

const NoUnderlineLink = styled(Link)`
  text-decoration: none;
`;

interface LoadingProps {
  isLogin?: boolean;
}

const Loading = ({ isLogin }: LoadingProps) => {
  return (
    <>
      <Header isLogin={isLogin} />
      <Wrap>
        <LoadingWrap className="do-loader">
          <LoadingState sammy={true} />
        </LoadingWrap>
      </Wrap>
    </>
  );
};

interface ButtonProps {
  url: string;
  text: string;
}

const GoogleButton = ({ url, text }: ButtonProps) => {
  return (
    <SignInButton
      fullWidth
      variation="secondary"
      onClick={() => window.location.assign(url)}
    >
      <ButtonImageBg>
        <ButtonImage className="u-mr--regular" src={oauthLogoGoogleSrc} />
      </ButtonImageBg>
      {text}
    </SignInButton>
  );
};

const GithubButton = ({ url, text }: ButtonProps) => {
  return (
    <GHButton fullWidth onClick={() => window.location.assign(url)}>
      <ButtonImage src={oauthLogoGithubSrc} />
      {text}
    </GHButton>
  );
};

interface Err {
  error: {
    title: string;
    details: string;
  } | null;
}
const ErrorBanner = (errObject: Err) => {
  const error = _get(errObject, 'error', copy.errors.unkown);
  const contactLinkKey = '{{contact}}';
  const contactLinkIndex = error.details.indexOf(contactLinkKey);
  if (contactLinkIndex > -1) {
    return (
      <Banner type="error" {...error}>
        {error.details.slice(0, contactLinkIndex)}
        <DOLink path="/support/contact/">contact support</DOLink>
        {error.details.slice(contactLinkIndex + contactLinkKey.length)}
      </Banner>
    );
  } else {
    return <Banner type="error" {...error} />;
  }
};

interface Click {
  onClick: () => any;
  disabled?: boolean;
  children: React.ReactNode;
}
const ClickLink = ({ onClick, children, disabled }: Click) => {
  return (
    <NoUnderlineLink
      role="button"
      to="/login"
      includeContext={false}
      tabIndex={0}
      disabled={disabled}
      onClick={(e: any) => {
        onClick();
        e.preventDefault();
      }}
    >
      {children}
    </NoUnderlineLink>
  );
};

enum BannerType {
  Success = 'alert-success'
}

interface HeaderBannerProps {
  type: BannerType;
  text: string;
  linkText?: string;
  url?: string;
}

const BannerWithIcon = styled.div`
  position: fixed;
  top: 65px;
  right: 0;
  left: 0;
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 12px;
  padding: ${vars.whitespace_small} ${vars.whitespace_medium};
  align-items: center;
  z-index: 2;
  flex-wrap: none;
  background: ${colors.green_lightest};
`;

const HeaderBanner = ({
  type,
  text,
  linkText,
  url = ''
}: HeaderBannerProps) => (
  <BannerWithIcon>
    <Icon icon={type} large={true} />
    <Text type="p">
      <strong>{text}</strong>
      {linkText && (
        <Link to={url} type="external">
          {linkText}
        </Link>
      )}
    </Text>
  </BannerWithIcon>
);

export {
  Wrap,
  LoadingWrap,
  FormWrap,
  SignInButton,
  GithubButton,
  GoogleButton,
  ButtonImage,
  PaddedContents,
  ErrorBanner,
  ClickLink,
  NoUnderlineLink,
  Loading,
  HeaderBanner,
  BannerType
};
